// 파티 던전 프로토콜·규칙(docs/PARTY-TRADE-PRD.md 3장, 레인 D). 서버(src/server/party.ts PartyRoom)와 클라(src/game/partyNet.ts)가 같이 쓴다.
// - 넷코드: 서버 권위 몹(위치·체력·공격 예고·사망·보상) + 클라 권위 플레이어(자기 영웅 이동·체력). 같은 판을 둘 이상이 본다.
// - 메시지는 JSON 텍스트(인원 2~4명, 몹 ≤ 16 — 10Hz 스냅 ≈ 0.5KB, 이진 FieldNet보다 단순한 쪽을 골랐다). 좌표는 m(소수 2자리).
// - 몹 두뇌(stepPartyMob)는 순수 함수다: 서버가 10Hz로 돌리고, 시험이 같은 함수를 부른다.
// 순수 데이터·함수만 둔다(DOM·Workers API 없음).
import { DAMAGE_CAP, MOB_STATS, MOBS } from './config';
import { REGION_HP_MULT } from './content/world';
import { STAGE_STEP } from './growth';
/** stages.ts stageMul과 같은 식(지도 묶음을 Worker에 끌어오지 않으려고 따로 둔다 — partyNet.test.ts가 같음을 확인) */
const stageMul = (index: number): number => Math.round((1 + STAGE_STEP * (Math.max(1, index) - 1)) * 100) / 100;
import { REGIONS } from './regions';
import { hashSeed, nextRand, rollGear, rollNamedItem, seedRng, type GearItem } from './gear';
import { NAMED_UNIQUES } from './content/items';
import type { HeroId, RegionNo } from './content/types';
import { MobKind } from './types';

/** 프로토콜 버전(메시지 모양이 바뀌면 올린다 — 다른 버전 클라는 err 'version'으로 닫는다) */
export const PARTY_PROTO_VERSION = 1;
export const PARTY_WS_PATH = '/api/party/ws';
/** 파티 코드: 헷갈리는 글자(0·O·1·I·L)를 뺀 6자 */
export const PARTY_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const PARTY_CODE_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{6}$/;
/** 정원 상한(3단계 4인). 운영 정원은 서버 var PARTY_MAX(1단계 기본 2) */
export const PARTY_CAP = 4;
export const PARTY_MAX_DEFAULT = 2;
/** 서버 틱(판 진행 중에만, DO 알람) */
export const PARTY_TICK_MS = 100;
export const PARTY_COUNTDOWN_MS = 3000;
/** 판 제한 시간 */
export const PARTY_RUN_MAX_MS = 10 * 60_000;
/** 끊긴 사람 자리 유지 */
export const PARTY_GRACE_MS = 60_000;
/** 쓰러진 뒤 일어나기(파티 판 전용) */
export const PARTY_REVIVE_MS = 8000;
/** 아무도 없는 방을 닫기까지 */
export const PARTY_IDLE_CLOSE_MS = 10 * 60_000;
/** 서 있을 때 생존 확인(텍스트 'ping' → 런타임 자동 'pong') */
export const PARTY_KEEPALIVE_MS = 25_000;
/** 하루 보상 판 수(KST 날짜) */
export const PARTY_DAILY_REWARDS = 5;
/** 보상 자격: 전체 피해 중 이 비율 이상 */
export const PARTY_ELIGIBLE_RATIO = 0.05;
/** 판 보상에 이름 있는 유니크(거래 가능)가 들어갈 확률(가안 — 보스 직접 드롭 0.03보다 조금 높게: 같이 하는 쪽이 이득) */
export const PARTY_UNIQUE_CHANCE = 0.06;
export const PARTY_NAME_MAX = 12;
/** 클라 메시지 최대 바이트(가장 긴 hello ≈ 120B) */
export const PARTY_MAX_IN_BYTES = 512;

/** 이동 검사(FieldShard와 같은 거리 토큰 통) */
export const PARTY_MOVE = { maxSpeed: 12, burst: 10, epsilon: 0.25 } as const;
/**
 * 타격 검사: 사람당 초당 hitsPerSec(최대 burst), 몹과 서버 위치 거리 range(m) 안. 거부 rejectKick번이면 내보낸다.
 * range는 원거리 영웅(라이라 화살 최대 18m) + 몹 반경 + 지연 여유(PR #243 리뷰 P1). 사거리 거부는 내보내기 횟수에 세지 않는다
 */
// hitsPerSec·burst: 광역 다단 스킬(칼날 폭풍 150ms × 6회 × 8마리 = 한 번에 48타)을 받아 준다. 피해량은 레벨 피해 통이 따로 막는다.
// 빈도 초과 타격은 버리기만 하고 내보내기 횟수에 세지 않는다(정상 몹몰이 추방 방지 — PR #243 Codex P1)
export const PARTY_HIT = { hitsPerSec: 80, burst: 160, range: 22, rejectKick: 40 } as const;
/** 로비 메시지(hello·ready·tier·start·kick 등) 빈도 통: 초당 4개, 최대 12개. 넘치면 버리고 거부로 센다(PR #243 리뷰 P2) */
export const PARTY_MSG = { perSec: 4, burst: 12 } as const;
/** 판 난이도(지역)별로 인정하는 최대 레벨(클라가 말한 레벨을 그대로 믿지 않는다 — 피해 통 상한, PR #243 리뷰 P1) */
/** 서버가 세는 대략 체력(가안: 영웅 최대 체력의 넉넉한 위 — 방어·회피는 클라 몫이라 2배로 잡는다) */
export function partyServerHp(level: number): number { return Math.round((300 + 45 * Math.max(1, Math.min(99, level))) * 2); }
/** 판 지역 r을 고르려면 깬 스테이지가 이만큼(앞 지역 10스테이지씩) */
export function partyTierMinStages(region: number): number { return Math.max(0, (Math.max(1, Math.min(4, Math.floor(region) || 1)) - 1) * 10); }
/** 서버 저장 진행(깬 스테이지 수)으로 인정하는 최대 레벨(가안: 스테이지당 2.5 + 10) */
export function partyLevelByStages(stages: number): number { return Math.max(1, Math.min(99, Math.round(10 + 2.5 * Math.max(0, stages)))); }
export function partyLevelCap(region: number): number { return Math.max(1, Math.min(99, 15 + 20 * (Math.max(1, Math.min(4, Math.floor(region) || 1))))); }
/** 레벨 기반 초당 피해 상한(가안). 버스트 = 3초분. 넘는 피해는 남은 만큼만 인정 */
export function partyDpsCap(level: number): number {
  const lv = Math.max(1, Math.min(99, Math.floor(level) || 1));
  return 600 + 260 * lv;
}

/** 아레나: 지역1 보스방(MAPS.r1 「1-보스 고목 수호자」 area −20..20 × −170..−132)을 벽 1.5m 안쪽으로. 문은 파티 판에서 닫힌다 */
export const PARTY_ARENA = {
  map: 'r1' as const,
  x0: -18.5, z0: -168.5, x1: 18.5, z1: -133.5,
  /** 플레이어 자리(칸 번호 순) */
  spawns: [{ x: -2, z: -137 }, { x: 2, z: -137 }, { x: -5, z: -139 }, { x: 5, z: -139 }] as const,
  /** 몹 입구(아레나 안쪽 둘레) */
  entries: [
    { x: -16, z: -166 }, { x: 0, z: -167 }, { x: 16, z: -166 },
    { x: -17, z: -150 }, { x: 17, z: -150 }, { x: -12, z: -160 }, { x: 12, z: -160 },
  ] as const,
} as const;

/** 웨이브 구성: [종류, 정예] 목록. 마지막 웨이브의 정예 멧돼지가 우두머리 */
export const PARTY_WAVES: readonly (readonly (readonly [number, boolean])[])[] = [
  [[MobKind.Goblin, false], [MobKind.Goblin, false], [MobKind.Goblin, false], [MobKind.Goblin, false], [MobKind.Goblin, false]],
  [[MobKind.Goblin, false], [MobKind.Goblin, false], [MobKind.Goblin, false], [MobKind.ShieldGoblin, false], [MobKind.ShieldGoblin, false], [MobKind.Boar, false], [MobKind.Shaman, false]],
  [[MobKind.Goblin, false], [MobKind.Goblin, false], [MobKind.ShieldGoblin, false], [MobKind.ShieldGoblin, false], [MobKind.Shaman, false], [MobKind.Shaman, false], [MobKind.Boar, false], [MobKind.Boar, true]],
];
export const PARTY_MOB_MAX = 16;
/** 웨이브 사이 쉬는 시간 */
export const PARTY_WAVE_GAP_MS = 2500;

/** 인원별 몹 체력 배율 */
export const PARTY_HP_MULT: readonly number[] = [1, 1, 1.7, 2.3, 2.8];
/** 판 난이도: 지역 1..4, 그 지역 5스테이지 배율(stages.ts anyStagePlan과 같은 식) */
export const PARTY_TIER_STAGE = 5;
export interface PartyTier { region: RegionNo; hpMult: number; dmgMult: number; goldBase: number; goldPerKill: number }
export function partyTier(region: number): PartyTier {
  const r = (Math.max(1, Math.min(4, Math.floor(region) || 1))) as RegionNo;
  const mul = stageMul(PARTY_TIER_STAGE);
  const info = REGIONS[('r' + r) as keyof typeof REGIONS];
  const goldBase = [1500, 2600, 4200, 6400][r - 1];
  return {
    region: r,
    hpMult: Math.round(REGION_HP_MULT[r - 1] * mul * 1000) / 1000,
    dmgMult: Math.round(info.mobDamageMult * mul * 1000) / 1000,
    goldBase,
    goldPerKill: Math.round(goldBase / 120),
  };
}

/** 몹 한 마리의 최대 체력(서버가 정하고 spawn으로 보낸다) */
export function partyMobHp(kind: number, elite: boolean, tier: PartyTier, members: number): number {
  const base = MOB_STATS[kind]?.hp ?? MOB_STATS[0].hp;
  return Math.max(1, Math.round(base * tier.hpMult * (elite ? MOBS.elite.hpMult : 1) * (PARTY_HP_MULT[Math.max(1, Math.min(4, members))] ?? 1)));
}
/** 몹 한 번 공격 피해(클라가 자기 영웅에 적용 — 방어·회피는 클라 규칙) */
export function partyMobDamage(kind: number, elite: boolean, tier: PartyTier): number {
  const base = MOB_STATS[kind]?.damage ?? MOB_STATS[0].damage;
  return Math.max(1, Math.round(base * tier.dmgMult * (elite ? 1.5 : 1)));
}

// ── 몹 두뇌(서버 10Hz) ─────────────────────────────────

export const PMobSt = { Spawn: 0, Chase: 1, Windup: 2, Recover: 3, Dead: 4 } as const;
/** 등장 연출 시간 */
export const PARTY_SPAWN_MS = 700;
/** 공격 예고 판정 반경(근접은 사거리 + 0.6, 멧돼지 돌진은 선 대신 끝점 원, 주술사는 포격 원) */
export function partyAtkRadius(kind: number): number {
  if (kind === MobKind.Shaman) return 2.0;
  if (kind === MobKind.Boar) return 1.6;
  return (MOB_STATS[kind]?.attackRange ?? 1.3) + 0.6;
}
const TICK_TO_MS = 1000 / 60;

export interface PartyMob {
  id: number;
  kind: number;
  elite: boolean;
  x: number; z: number; f: number;
  hp: number; max: number;
  st: number;
  /** 지금 상태에서 지난 시간(ms) */
  t: number;
  /** 다음 공격까지(ms) */
  cd: number;
  /** 공격 예고 중심·반경·판정까지(ms) */
  ax: number; az: number; ar: number;
  /** 노리는 칸(-1 없음) */
  target: number;
}
export interface PartyActor { slot: number; x: number; z: number; alive: boolean }
/** stepPartyMob이 내는 일 */
export const PMobEv = { None: 0, Telegraph: 1, Strike: 2 } as const;

export function makePartyMob(id: number, kind: number, elite: boolean, x: number, z: number, max: number): PartyMob {
  return { id, kind, elite, x, z, f: 0, hp: max, max, st: PMobSt.Spawn, t: 0, cd: 600 + (id % 5) * 180, ax: 0, az: 0, ar: 0, target: -1 };
}

function clampArena(m: PartyMob, radius: number): void {
  const a = PARTY_ARENA;
  m.x = Math.max(a.x0 + radius, Math.min(a.x1 - radius, m.x));
  m.z = Math.max(a.z0 + radius, Math.min(a.z1 - radius, m.z));
}

/**
 * 몹 한 마리를 dtMs만큼 진행한다. 가장 가까운 살아 있는 플레이어를 쫓고, 사거리에 들면 예고(windup) → 판정(Strike) → 쿨다운.
 * 주술사는 keepAway(6m) 밖에서 포격, 멧돼지는 6m에서 돌진 예고(끝점 원). 몹끼리 간단히 밀어낸다(others).
 * 반환: PMobEv(예고 시작이면 Telegraph, 판정 순간이면 Strike). 할당 없음.
 */
export function stepPartyMob(m: PartyMob, actors: readonly PartyActor[], others: readonly PartyMob[], dtMs: number): number {
  if (m.st === PMobSt.Dead) return PMobEv.None;
  const s = MOB_STATS[m.kind] ?? MOB_STATS[0];
  m.t += dtMs;
  if (m.st === PMobSt.Spawn) {
    if (m.t >= PARTY_SPAWN_MS) { m.st = PMobSt.Chase; m.t = 0; }
    return PMobEv.None;
  }
  // 대상: 가장 가까운 살아 있는 플레이어
  let best = -1, bd = Infinity, tx = 0, tz = 0;
  for (const a of actors) {
    if (!a.alive) continue;
    const dx = a.x - m.x, dz = a.z - m.z, d = dx * dx + dz * dz;
    if (d < bd) { bd = d; best = a.slot; tx = a.x; tz = a.z; }
  }
  m.target = best;
  if (m.st === PMobSt.Windup) {
    if (m.t >= s.windup * TICK_TO_MS) { m.st = PMobSt.Recover; m.t = 0; m.cd = s.cooldown * TICK_TO_MS; return PMobEv.Strike; }
    return PMobEv.None;
  }
  if (m.st === PMobSt.Recover) {
    if (m.t >= s.attackTicks * TICK_TO_MS + 150) { m.st = PMobSt.Chase; m.t = 0; }
    return PMobEv.None;
  }
  // 쫓기
  m.cd = Math.max(0, m.cd - dtMs);
  if (best < 0) return PMobEv.None;
  const dist = Math.sqrt(bd);
  const dx = tx - m.x, dz = tz - m.z;
  m.f = Math.atan2(dx, dz);
  const shaman = m.kind === MobKind.Shaman;
  const reach = shaman ? s.attackRange : s.attackRange + s.radius * 0.5;
  if (dist <= reach && m.cd <= 0) {
    m.st = PMobSt.Windup; m.t = 0; m.ar = partyAtkRadius(m.kind);
    if (shaman || m.kind === MobKind.Boar) { m.ax = tx; m.az = tz; }
    else { const k = dist > 0.001 ? Math.min(dist, s.attackRange) / dist : 0; m.ax = m.x + dx * k; m.az = m.z + dz * k; }
    return PMobEv.Telegraph;
  }
  const step = s.speed * (dtMs / 1000);
  let mx = 0, mz = 0;
  if (shaman && dist < 6) { mx = -dx / Math.max(dist, 0.001) * step * 0.85; mz = -dz / Math.max(dist, 0.001) * step * 0.85; }
  else if (dist > reach * 0.85) { const k = Math.min(step, dist - reach * 0.85) / Math.max(dist, 0.001); mx = dx * k; mz = dz * k; }
  // 몹끼리 겹치지 않게 살짝 밀어낸다
  for (const o of others) {
    if (o === m || o.st === PMobSt.Dead) continue;
    const ox = m.x - o.x, oz = m.z - o.z, od = ox * ox + oz * oz, min = s.radius + (MOB_STATS[o.kind]?.radius ?? 0.45);
    if (od > 0.0001 && od < min * min) { const k = (min - Math.sqrt(od)) * 0.5 / Math.sqrt(od); mx += ox * k; mz += oz * k; }
  }
  m.x += mx; m.z += mz;
  clampArena(m, s.radius);
  return PMobEv.None;
}

// ── 보상(서버 RNG) ─────────────────────────────────────

/** KST 날짜(YYYYMMDD 정수) — 하루 보상 횟수 */
export function kstDay(ms: number): number {
  const d = new Date(ms + 9 * 3_600_000);
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}
export interface PartyReward { gold: number; items: GearItem[] }
/**
 * 한 사람의 판 보상. 씨앗 = 판 id + 계정 키(새로고침·재전송으로 다시 굴릴 수 없다). 장비: 희귀 이상 1개 확정 + 20% 1개 더(보스 표).
 * uid = srv:<판 id>:<계정 키 앞 8>:<n> — 서버 발급 개체(거래소 인증 장비)
 */
export function partyReward(runId: string, who: string, hero: HeroId, tier: PartyTier, kills: number, salt = ''): PartyReward {
  // salt = 서버만 아는 판 비밀(클라가 공개 runId로 결과를 미리 계산하지 못하게). uid에는 들어가지 않는다
  const r = seedRng(hashSeed('party:' + runId + ':' + who + (salt ? ':' + salt : '')));
  const rand = () => nextRand(r);
  const ctx = { region: tier.region, diff: 'normal' as const, table: 'drop_mid' as const, hero };
  const tag = who.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
  const items: GearItem[] = [rollGear(rand, ctx, 'boss', `srv:${runId}:${tag}:0`, 0, { min: 'rare', max: 'legend' })];
  if (rand() < 0.2) items.push(rollGear(rand, ctx, 'boss', `srv:${runId}:${tag}:1`, 0, { min: 'rare', max: 'legend' }));
  // 이름 있는 유니크(거래 가능, 레인 B NAMED_UNIQUES): 판마다 PARTY_UNIQUE_CHANCE. 그 지역 것을 먼저, 없으면 거래 가능 전체에서.
  // 서버가 만든 유니크라 거래소 인증 장비(MARKET_CLIENT_ITEMS=false에서도 등록 가능)다. 위 굴림 뒤에 굴려 기존 결과를 바꾸지 않는다
  if (rand() < PARTY_UNIQUE_CHANCE) {
    const pool = NAMED_UNIQUES.filter((u) => u.tradable && u.source.region === tier.region);
    const list = pool.length ? pool : NAMED_UNIQUES.filter((u) => u.tradable);
    const u = list[Math.min(list.length - 1, Math.floor(rand() * list.length))];
    const it = u ? rollNamedItem(rand, u, ctx, `srv:${runId}:${tag}:u`) : null;
    if (it) items.push(it);
  }
  const gold = Math.round(tier.goldBase + tier.goldPerKill * Math.max(0, Math.min(200, kills)));
  return { gold, items };
}

// ── 메시지 ─────────────────────────────────────────────

/** 방 단계 */
export type PartyPhase = 'lobby' | 'count' | 'run' | 'end';
export interface PartyMemberView { slot: number; name: string; hero: HeroId; lv: number; ready: boolean; online: boolean; down: boolean; leader: boolean }
export interface PartyRoomView { code: string; phase: PartyPhase; max: number; members: PartyMemberView[]; tier: number; runId: string; wave: number; waves: number; startAt: number; rewardsLeft: number }

/** 클라 → 서버 */
export type PartyClientMsg =
  | { t: 'hello'; v: number; name: string; hero: HeroId; lv: number }
  | { t: 'ready'; on: boolean }
  | { t: 'tier'; r: number }
  | { t: 'start' }
  | { t: 'leave' }
  | { t: 'kick'; slot: number }
  | { t: 'pos'; x: number; z: number; f: number; a: number }
  | { t: 'hit'; m: number; d: number }
  | { t: 'down' }
  | { t: 'up' };

/** 서버 → 클라 */
export type PartyServerMsg =
  | { t: 'room'; room: PartyRoomView; you: number }
  | { t: 'err'; code: string }
  | { t: 'go'; runId: string; tier: number; at: number; seed: number }
  /** 몹 등장: [id, 종류, 정예 0/1, x, z, 체력, 최대 체력] */
  | { t: 'spawn'; w: number; ms: [number, number, number, number, number, number, number][] }
  /** 스냅: p = [칸, x, z, 방향, 동작], m = [id, x, z, 체력, 상태, 방향] */
  | { t: 's'; k: number; p: [number, number, number, number, number][]; m: [number, number, number, number, number, number][] }
  | { t: 'atk'; id: number; x: number; z: number; r: number; ms: number }
  /** 몹 판정: 이 원 안의 영웅이 d 피해(맞은 칸 목록) */
  | { t: 'hurt'; id: number; slots: number[]; d: number }
  | { t: 'die'; id: number; by: number }
  | { t: 'wave'; n: number; of: number }
  | { t: 'correct'; x: number; z: number }
  | { t: 'end'; ok: boolean; reason: string; runId: string; kills: number; ms: number; dmg: number[]; eligible: boolean; reward: { gold: number; items: number; vault?: boolean } | null };

/** 좌표 반올림(소수 2자리) */
export const q2 = (v: number): number => Math.round(v * 100) / 100;
/** 피해 상한(게임 맥뎀) */
export const PARTY_DAMAGE_CAP = DAMAGE_CAP;

/** 이름 정리: 제어문자·꺾쇠 제거, 앞뒤 공백, 최대 12자 */
export function cleanPartyName(raw: unknown): string {
  const s = typeof raw === 'string' ? raw.replace(/[\u0000-\u001f\u007f<>]/g, '').trim() : '';
  return [...s].slice(0, PARTY_NAME_MAX).join('') || '모험가';
}
const HEROES: readonly string[] = ['kael', 'lyra', 'seren', 'brann', 'lumen'];
export function validHero(v: unknown): v is HeroId { return typeof v === 'string' && HEROES.includes(v); }
export const validCoordP = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 2000;
